// ContactoFormSection.js — Página «Contáctanos» (tipo `contacto_form`) con el
// lenguaje del catálogo streaming: banner con las portadas de los cursos, canales
// de contacto a la izquierda y el formulario (nombre, correo, tema, mensaje) en
// una tarjeta de vidrio. Los mensajes se guardan (admin → ficha → Mensajes) y se
// avisa por correo a `destinatarios`. Sin captcha: un campo trampa invisible.
import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import MuroCursos from './MuroCursos';

const FUENTE = "'Bricolage Grotesque', 'Inter', system-ui, sans-serif";

export const CONTACTO_DEF = {
  eyebrow: 'Contáctanos',
  titulo: 'Hablemos',
  texto: 'Escríbenos si tienes dudas sobre un curso, tu certificado, tu cuenta o si quieres llevar Ateneo Digital a tu institución. Te respondemos por correo.',
  canales: [
    { icono: 'correo', titulo: 'Correo', valor: 'administrativa@ateneo.co', url: 'mailto:administrativa@ateneo.co' },
  ],
  temas: ['Un curso', 'Mi certificado', 'Mi cuenta o acceso', 'Pagos', 'Empresas e instituciones', 'Otro'],
  respuesta: 'Te respondemos en 1 a 2 días hábiles',
  ayuda_titulo: 'Antes de escribir',
  ayuda: [
    { pregunta: '¿Olvidaste tu contraseña?', texto: 'Recupérala en un minuto con el correo de tu cuenta.', boton: 'Recuperar contraseña', url: '/recuperar-contrasena' },
    { pregunta: '¿Dónde está mi certificado?', texto: 'Cuando completas un curso y apruebas sus exámenes, lo descargas en Mis certificados.', boton: 'Ver mis certificados', url: '/certificados' },
    { pregunta: '¿Cómo retomo un curso?', texto: 'Tu avance se guarda solo: entra a tu progreso y sigue donde quedaste.', boton: 'Ir a mi progreso', url: '/contenidos' },
  ],
  texto_boton: 'Enviar mensaje',
  exito_titulo: 'Recibimos tu mensaje',
  exito_texto: 'Te responderemos al correo que dejaste.',
};

const P = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round', strokeLinejoin: 'round' };
export const ICONOS_CONTACTO = {
  correo: <svg viewBox="0 0 24 24" {...P}><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m4 7 8 6 8-6" /></svg>,
  whatsapp: <svg viewBox="0 0 24 24" {...P}><path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.1A8 8 0 1 1 20 12Z" /><path d="M9.2 9.3c.3 1.9 1.6 3.8 3.9 4.8l1.1-1.1 1.6.7-.4 1.4c-3.7.2-7-3-7-6.6l1.4-.4.7 1.6-1.3.6" /></svg>,
  telefono: <svg viewBox="0 0 24 24" {...P}><path d="M5 4h3.5l1.5 4.5-2 1.2a10 10 0 0 0 5.3 5.3l1.2-2 4.5 1.5V18a2 2 0 0 1-2 2A15 15 0 0 1 3 6a2 2 0 0 1 2-2Z" /></svg>,
  ubicacion: <svg viewBox="0 0 24 24" {...P}><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></svg>,
  horario: <svg viewBox="0 0 24 24" {...P}><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></svg>,
};

// Icono de un tema según sus palabras (los temas son texto libre).
const iconoTema = (t) => {
  const k = t.toLowerCase();
  const d = /certific/.test(k) ? 'M12 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm-2.5 9.3-1.5 7.2 4-2.2 4 2.2-1.5-7.2'
    : /cuenta|acceso|contrase/.test(k) ? 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8ZM4.5 20.5c.9-3.6 3.9-6 7.5-6s6.6 2.4 7.5 6'
    : /pago|factur|precio/.test(k) ? 'M3 7h18v10H3zM3 11h18M7 15h3'
    : /empresa|instituc/.test(k) ? 'M4 21V5l8-2v18M12 9h8v12M7 8h2M7 12h2M7 16h2M15 13h2M15 17h2'
    : /curso|lecci/.test(k) ? 'M3 5h18v12H3zM10.5 8.8l4 2.2-4 2.2Z'
    : 'M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.1A8 8 0 1 1 20 12Z';
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
};

const Raiz = styled.div`--acento: ${p => p.$acento}; background: ${p => p.$fondo}; color: #d5d9df; font-family: ${FUENTE};`;
const Cabecera = styled.header`
  position: relative; overflow: hidden; isolation: isolate; min-height: clamp(360px, 46vh, 480px);
  display: flex; flex-direction: column; justify-content: flex-end; padding: clamp(8rem, 12vw, 9rem) 4vw clamp(2rem, 4vw, 3rem);
  .eyebrow { display: inline-flex; align-items: center; gap: .6rem; font-size: .92rem; font-weight: 600; color: #e7eaee; }
  .eyebrow::before { content: ''; width: 28px; height: 2px; background: var(--acento); }
  h1 { margin: .9rem 0 0; font-size: clamp(3rem, 8vw, 6.2rem); font-weight: 800; font-stretch: 76%; line-height: .9; letter-spacing: -.02em; color: #fff; }
`;
const Cuerpo = styled.div`
  display: grid; grid-template-columns: minmax(0, .9fr) minmax(0, 1.3fr); grid-template-rows: auto 1fr;
  grid-template-areas: 'info form' 'ayuda form'; gap: 0 clamp(2rem, 5vw, 5rem); align-items: start;
  padding: clamp(1rem, 3vw, 2rem) 4vw clamp(4rem, 7vw, 6rem);
  > .info { grid-area: info; } > .form { grid-area: form; } > .ayuda { grid-area: ayuda; }
  > * { min-width: 0; }
  @media (max-width: 900px) { grid-template-columns: 1fr; grid-template-rows: none; grid-template-areas: 'info' 'form' 'ayuda'; gap: 2rem; }
`;
const Info = styled.div`
  > p { margin: 0 0 2rem; max-width: 34rem; font-size: 1.08rem; line-height: 1.65; color: #b9c0ca; }
  ul { list-style: none; margin: 0; padding: 0; display: grid; gap: .8rem; }
  li { position: relative; }
  .copiar { position: absolute; right: .8rem; top: 50%; transform: translateY(-50%); display: inline-flex; align-items: center; gap: .35rem;
    height: 34px; padding: 0 .8rem; border: 0; border-radius: 999px; cursor: pointer; font: inherit; font-size: .8rem; font-weight: 700;
    background: rgba(255,255,255,.08); color: #e7eaee; transition: background .15s; }
  .copiar:hover { background: rgba(255,255,255,.16); }
  .copiar:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  .copiar svg { width: 15px; height: 15px; }
  li:has(.copiar) a { padding-right: 7.5rem; }
  @media (max-width: 480px) { .copiar { width: 38px; padding: 0; justify-content: center; font-size: 0; gap: 0; } .copiar svg { width: 17px; height: 17px; } li:has(.copiar) a { padding-right: 3.8rem; } }
  a, .canal {
    display: flex; align-items: center; gap: 1rem; padding: 1rem 1.1rem; border-radius: 16px; text-decoration: none; color: inherit;
    background: rgba(255,255,255,.04); box-shadow: inset 0 0 0 1px rgba(255,255,255,.08); transition: background .2s, box-shadow .2s, transform .2s;
  }
  a:hover { background: rgba(255,255,255,.07); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 55%, transparent); transform: translateX(4px); }
  a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  i { display: grid; place-items: center; flex: none; width: 46px; height: 46px; border-radius: 14px;
    background: color-mix(in srgb, var(--acento) 16%, transparent); color: var(--acento); }
  svg { width: 22px; height: 22px; }
  small { display: block; font-size: .8rem; color: #8a929d; }
  strong { display: block; font-size: 1rem; font-weight: 700; color: #fff; overflow-wrap: anywhere; }
`;
const Respuesta = styled.p`
  display: inline-flex; align-items: center; gap: .55rem; margin: 1.1rem 0 0 !important; font-size: .9rem !important; color: #c6ccd4 !important;
  svg { width: 18px; height: 18px; color: var(--acento); }
  i { position: relative; width: 8px; height: 8px; border-radius: 50%; background: #3ddc84; box-shadow: 0 0 0 4px rgba(61,220,132,.18); }
`;
const Ayuda = styled.div`
  margin-top: 2.4rem;
  @media (max-width: 900px) { margin-top: 0; }
  h2 { margin: 0 0 .9rem; font-size: 1.05rem; font-weight: 700; color: #fff; }
  a {
    display: grid; grid-template-columns: 1fr auto; gap: .2rem 1rem; align-items: center; padding: .95rem 1.1rem; margin-bottom: .6rem;
    border-radius: 14px; text-decoration: none; color: inherit; background: none; box-shadow: inset 0 0 0 1px rgba(255,255,255,.08);
    transition: background .2s, box-shadow .2s;
  }
  a:hover { background: rgba(255,255,255,.04); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--acento) 50%, transparent); }
  a:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
  strong { font-size: .95rem; color: #fff; }
  span { grid-column: 1; font-size: .86rem; line-height: 1.45; color: #9aa1ab; }
  em { grid-row: 1 / span 2; grid-column: 2; display: inline-flex; align-items: center; gap: .3rem; font-style: normal; font-size: .82rem; font-weight: 700;
    color: var(--acento); white-space: nowrap; transition: gap .2s; }
  a:hover em { gap: .55rem; }
  em svg { width: 15px; height: 15px; }
  @media (max-width: 480px) { a { grid-template-columns: 1fr; } em { grid-row: auto; grid-column: 1; margin-top: .4rem; } }
`;
const Tarjeta = styled.div`
  position: relative; padding: clamp(1.4rem, 3vw, 2.2rem); border-radius: 22px;
  background: linear-gradient(160deg, rgba(255,255,255,.07), rgba(255,255,255,.025));
  box-shadow: inset 0 0 0 1px rgba(255,255,255,.1), 0 30px 80px -40px rgba(0,0,0,.9);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
`;
const Fila = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; @media (max-width: 560px) { grid-template-columns: 1fr; }`;
const Campo = styled.label`
  display: block; margin-bottom: 1.1rem;
  > span { display: block; margin-bottom: .45rem; font-size: .88rem; font-weight: 600; color: #e7eaee; }
  input, textarea {
    width: 100%; box-sizing: border-box; padding: .9rem 1rem; border-radius: 12px; border: 0; font: inherit; font-size: 1rem; color: #fff;
    background: rgba(0,0,0,.35); box-shadow: inset 0 0 0 1px ${p => (p.$error ? '#ff6b6b' : 'rgba(255,255,255,.12)')};
    transition: box-shadow .2s, background .2s;
  }
  textarea { min-height: 150px; resize: vertical; line-height: 1.55; }
  input::placeholder, textarea::placeholder { color: #6f7782; }
  input:focus, textarea:focus { outline: none; background: rgba(0,0,0,.45); box-shadow: inset 0 0 0 2px var(--acento); }
  .pie { display: flex; justify-content: space-between; gap: 1rem; margin-top: .35rem; font-size: .8rem; }
  .error { color: #ff8a8a; }
  .cuenta { color: #6f7782; margin-left: auto; font-variant-numeric: tabular-nums; }
`;
const Temas = styled.fieldset`
  border: 0; margin: 0 0 1.1rem; padding: 0;
  legend { padding: 0; margin-bottom: .55rem; font-size: .88rem; font-weight: 600; color: #e7eaee; }
  div { display: flex; flex-wrap: wrap; gap: .5rem; }
  button { display: inline-flex; align-items: center; gap: .4rem; height: 38px; padding: 0 .95rem; border: 0; border-radius: 999px; cursor: pointer; font: inherit; font-size: .88rem; font-weight: 600;
    background: rgba(255,255,255,.06); color: #c9ced6; box-shadow: inset 0 0 0 1px rgba(255,255,255,.12); transition: background .15s, color .15s, box-shadow .15s; }
  button svg { width: 15px; height: 15px; }
  button:hover { background: rgba(255,255,255,.12); color: #fff; }
  button[aria-pressed='true'] { background: var(--acento); color: #fff; box-shadow: none; }
  button:focus-visible { outline: 2px solid var(--acento); outline-offset: 2px; }
`;
const Enviar = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem; width: 100%; height: 54px; border: 0; border-radius: 999px; cursor: pointer;
  background: var(--acento); color: #fff; font: inherit; font-size: 1.05rem; font-weight: 700; box-shadow: 0 14px 34px -14px var(--acento);
  transition: transform .15s ease, box-shadow .2s, opacity .2s;
  svg { width: 19px; height: 19px; transition: transform .25s ease; }
  &:hover:not(:disabled) { transform: translateY(-2px); } &:hover:not(:disabled) svg { transform: translateX(3px) rotate(-8deg); }
  &:disabled { opacity: .6; cursor: progress; }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
`;
const trazar = keyframes`from { stroke-dashoffset: 30; } to { stroke-dashoffset: 0; }`;
const aparecer = keyframes`from { opacity: 0; transform: scale(.94); } to { opacity: 1; transform: none; }`;
const Exito = styled.div`
  text-align: center; padding: 2.5rem 1rem; animation: ${aparecer} .35s ease;
  .check { display: grid; place-items: center; width: 84px; height: 84px; margin: 0 auto 1.3rem; border-radius: 50%;
    background: var(--acento); box-shadow: 0 16px 40px -14px var(--acento); color: #fff; }
  .check path { stroke-dasharray: 30; animation: ${trazar} .6s .15s ease-out both; }
  h2 { margin: 0 0 .5rem; font-size: 1.8rem; font-weight: 800; font-stretch: 82%; color: #fff; }
  p { margin: 0 auto 1.6rem; max-width: 26rem; color: #b9c0ca; }
  button { border: 0; background: none; color: #fff; font: inherit; font-weight: 600; text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 4px; cursor: pointer; }
  @media (prefers-reduced-motion: reduce) { animation: none; .check path { animation: none; } }
`;
const Sesion = styled.div`
  display: flex; align-items: center; gap: .8rem; margin-bottom: 1.3rem; padding: .7rem .8rem .7rem .7rem; border-radius: 14px;
  background: rgba(0,0,0,.3); box-shadow: inset 0 0 0 1px rgba(255,255,255,.1);
  .avatar { display: grid; place-items: center; flex: none; width: 40px; height: 40px; border-radius: 50%; background: var(--acento); color: #fff; font-weight: 800; }
  .quien { min-width: 0; flex: 1; overflow: hidden; }
  @media (max-width: 480px) { strong span { display: block; font-size: .85rem; overflow: hidden; text-overflow: ellipsis; } }
  small { display: block; font-size: .78rem; color: #8a929d; }
  strong { display: block; color: #fff; font-size: .95rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  strong span { font-weight: 500; color: #9aa1ab; }
  button { border: 0; background: none; color: #e7eaee; font: inherit; font-size: .85rem; font-weight: 600; cursor: pointer;
    text-decoration: underline; text-decoration-color: var(--acento); text-underline-offset: 3px; }
`;
const Selector = styled.select`
  width: 100%; height: 50px; padding: 0 2.6rem 0 1rem; border: 0; border-radius: 12px; font: inherit; font-size: 1rem; color: #fff; cursor: pointer;
  background: rgba(0,0,0,.35) url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' fill='none' stroke='%23c3c9d1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m4 7 5 5 5-5'/%3E%3C/svg%3E") no-repeat right 1rem center;
  appearance: none; box-shadow: inset 0 0 0 1px rgba(255,255,255,.12);
  &:focus { outline: none; box-shadow: inset 0 0 0 2px var(--acento); }
  option { background: #1d1d1d; color: #fff; }
`;
const Borrador = styled.span`font-size: .8rem; color: #8a929d;`;
const Aviso = styled.p`margin: 0 0 1rem; padding: .8rem 1rem; border-radius: 12px; background: rgba(255,107,107,.12); color: #ffb4b4; font-size: .92rem;`;
const Trampa = styled.input`position: absolute; left: -10000px; width: 1px; height: 1px; opacity: 0;`;

const ContactoFormSection = ({ config = {}, proyecto }) => {
  const c = { ...CONTACTO_DEF, ...Object.fromEntries(Object.entries(config).filter(([, v]) => v !== '' && v != null && !(Array.isArray(v) && !v.length))) };
  const { user } = useAuth();
  const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
  const acento = c.color_acento || cat.color_acento || '#E50914';
  const fondo = c.color_fondo || cat.color_fondo || '#141414';
  const [errores, setErrores] = useState({});
  const [estado, setEstado] = useState('');   // '', enviando, ok
  const [aviso, setAviso] = useState('');
  const claveBorrador = `contacto_borrador_${proyecto?.id || 0}`;
  const [f, setF] = useState(() => {
    // Tema y curso pueden venir en la dirección (?tema=certificado&curso=EPOC) y el borrador guardado.
    let b = {};
    try { b = JSON.parse(localStorage.getItem(claveBorrador) || '{}') || {}; } catch { b = {}; }
    const q = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const lista = (Array.isArray(c.temas) ? c.temas : []).filter(Boolean);
    const norm = (x) => String(x || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const temaUrl = q.get('tema') ? lista.find(t => norm(t).includes(norm(q.get('tema')))) : '';
    return { nombre: user?.nombre || user?.name || '', email: user?.email || '', asunto: temaUrl || b.asunto || '', curso: q.get('curso') || b.curso || '', mensaje: b.mensaje || '', sitio_web: '' };
  });
  const [editarDatos, setEditarDatos] = useState(false);
  const [copiado, setCopiado] = useState('');
  const [guardado, setGuardado] = useState(false);
  const cursos = useMemo(() => {
    const docs = proyecto?.documentos || [];
    return [...new Set((cat.items || []).map(it => it.titulo || docs.find(d => String(d.id) === String(it.documento_id))?.titulo).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, 'es'));
  }, [cat.items, proyecto]);
  const pideCurso = /curso/i.test(f.asunto) && cursos.length > 0;
  const conSesion = !!(user && (user.nombre || user.name) && user.email) && !editarDatos;

  // Si llega la sesión después de montar, se completan nombre y correo.
  useEffect(() => {
    if (user) setF(x => ({ ...x, nombre: x.nombre || user.nombre || user.name || '', email: x.email || user.email || '' }));
  }, [user]);
  // Borrador: tema, curso y mensaje se guardan en este navegador mientras se escribe.
  useEffect(() => {
    if (estado === 'ok') return undefined;
    const t = setTimeout(() => {
      try {
        if (f.mensaje.trim() || f.asunto) { localStorage.setItem(claveBorrador, JSON.stringify({ asunto: f.asunto, curso: f.curso, mensaje: f.mensaje })); setGuardado(!!f.mensaje.trim()); }
        else localStorage.removeItem(claveBorrador);
      } catch { /* sin almacenamiento: no pasa nada */ }
    }, 500);
    return () => clearTimeout(t);
  }, [f.asunto, f.curso, f.mensaje, estado, claveBorrador]);

  const copiar = async (texto) => {
    try { await navigator.clipboard.writeText(texto); setCopiado(texto); setTimeout(() => setCopiado(''), 1800); } catch { window.location.href = `mailto:${texto}`; }
  };
  const temas = useMemo(() => (Array.isArray(c.temas) ? c.temas : []).filter(Boolean), [c.temas]);
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); if (errores[k]) setErrores(e => ({ ...e, [k]: undefined })); };

  const validar = () => {
    const e = {};
    if (f.nombre.trim().length < 2) e.nombre = 'Escribe tu nombre.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = 'Escribe un correo válido, por ejemplo nombre@correo.com.';
    if (f.mensaje.trim().length < 10) e.mensaje = 'Cuéntanos un poco más (mínimo 10 caracteres).';
    return e;
  };
  const enviar = async (ev) => {
    ev.preventDefault();
    setAviso('');
    const e = validar();
    setErrores(e);
    if (Object.keys(e).length) {
      if ((e.nombre || e.email) && conSesion) setEditarDatos(true);
      setTimeout(() => document.getElementById(`contacto-${Object.keys(e)[0]}`)?.focus(), 0);
      return;
    }
    setEstado('enviando');
    try {
      const asunto = pideCurso && f.curso ? `${f.asunto} · ${f.curso}` : f.asunto;
      await axios.post(`${API_BASE_URL}/contacto.php`, { ...f, asunto, proyecto_id: proyecto?.id, origen: window.location.href });
      try { localStorage.removeItem(claveBorrador); } catch { /* nada */ }
      setGuardado(false);
      setEstado('ok');
    } catch (err) {
      setEstado('');
      if (err.response?.data?.errores) setErrores(err.response.data.errores);
      setAviso(err.response?.data?.message || 'No pudimos enviar tu mensaje. Revisa tu conexión e inténtalo de nuevo.');
    }
  };

  // Una lista vacía guardada a propósito significa «sin canales» (no vuelve el correo por defecto).
  const canales = (Array.isArray(config.canales) ? config.canales : CONTACTO_DEF.canales).filter(x => x.valor);
  const ayuda = (Array.isArray(c.ayuda) ? c.ayuda : []).filter(x => x.pregunta && x.url);
  return (
    <Raiz $acento={acento} $fondo={fondo}>
      <Cabecera>
        <MuroCursos proyecto={proyecto} fondo={fondo} acento={acento} imagen={c.imagen_banner} opacidad={0.65} />
        {c.eyebrow && <span className="eyebrow">{c.eyebrow}</span>}
        <h1>{c.titulo}</h1>
      </Cabecera>
      <Cuerpo>
        <Info className="info">
          {c.texto && <p>{c.texto}</p>}
          {canales.length > 0 && (
            <ul>
              {canales.map((x, i) => {
                const dentro = (<><i aria-hidden="true">{ICONOS_CONTACTO[x.icono] || ICONOS_CONTACTO.correo}</i><span><small>{x.titulo}</small><strong>{x.valor}</strong></span></>);
                const correo = /@/.test(x.valor) && !/^https?:/.test(x.valor) ? x.valor : '';
                return (
                  <li key={i}>
                    {x.url ? <a href={x.url} target={/^https?:/.test(x.url) ? '_blank' : undefined} rel="noopener noreferrer">{dentro}</a> : <div className="canal">{dentro}</div>}
                    {correo && (
                      <button type="button" className="copiar" onClick={() => copiar(correo)} aria-label={`Copiar ${correo}`}>
                        {copiado === correo
                          ? <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>Copiado</>
                          : <><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V6a2 2 0 0 1 2-2h9" /></svg>Copiar</>}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {('respuesta' in config ? config.respuesta : c.respuesta) && <Respuesta><i aria-hidden="true" />{c.respuesta}</Respuesta>}
        </Info>
        {ayuda.length > 0 && (
            <Ayuda className="ayuda">
              <h2>{c.ayuda_titulo}</h2>
              {ayuda.map((a, i) => (
                <Link key={i} to={a.url}>
                  <strong>{a.pregunta}</strong>
                  {a.texto && <span>{a.texto}</span>}
                  <em>{a.boton || 'Ver'}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg></em>
                </Link>
              ))}
            </Ayuda>
        )}
        <Tarjeta className="form">
          {estado === 'ok' ? (
            <Exito role="status">
              <div className="check"><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg></div>
              <h2>{c.exito_titulo}</h2>
              <p>{c.exito_texto} {f.email && <>(<b style={{ color: '#fff' }}>{f.email}</b>)</>}</p>
              <button type="button" onClick={() => { setF(x => ({ ...x, asunto: '', curso: '', mensaje: '' })); setEstado(''); }}>Escribir otro mensaje</button>
            </Exito>
          ) : (
            <form onSubmit={enviar} noValidate>
              {aviso && <Aviso role="alert">{aviso}</Aviso>}
              {conSesion ? (
                <Sesion>
                  <span className="avatar" aria-hidden="true">{(f.nombre || '?').trim().charAt(0).toUpperCase()}</span>
                  <span className="quien"><small>Escribes como</small><strong>{f.nombre} <span>· {f.email}</span></strong></span>
                  <button type="button" onClick={() => setEditarDatos(true)}>Cambiar</button>
                </Sesion>
              ) : (
              <Fila>
                <Campo $error={!!errores.nombre}>
                  <span>Nombre</span>
                  <input id="contacto-nombre" value={f.nombre} onChange={e => set('nombre', e.target.value)} autoComplete="name" placeholder="Tu nombre completo"
                    aria-invalid={!!errores.nombre} aria-describedby={errores.nombre ? 'err-nombre' : undefined} />
                  {errores.nombre && <div className="pie"><span className="error" id="err-nombre">{errores.nombre}</span></div>}
                </Campo>
                <Campo $error={!!errores.email}>
                  <span>Correo electrónico</span>
                  <input id="contacto-email" type="email" value={f.email} onChange={e => set('email', e.target.value)} autoComplete="email" placeholder="nombre@correo.com"
                    aria-invalid={!!errores.email} aria-describedby={errores.email ? 'err-email' : undefined} />
                  {errores.email && <div className="pie"><span className="error" id="err-email">{errores.email}</span></div>}
                </Campo>
              </Fila>
              )}
              {temas.length > 0 && (
                <Temas>
                  <legend>¿Sobre qué nos escribes?</legend>
                  <div>{temas.map(t => <button key={t} type="button" aria-pressed={f.asunto === t} onClick={() => set('asunto', f.asunto === t ? '' : t)}>{iconoTema(t)}{t}</button>)}</div>
                </Temas>
              )}
              {pideCurso && (
                <Campo>
                  <span>¿Cuál curso?</span>
                  <Selector id="contacto-curso" value={f.curso} onChange={e => set('curso', e.target.value)}>
                    <option value="">Elige el curso (opcional)</option>
                    {cursos.map(t => <option key={t} value={t}>{t}</option>)}
                  </Selector>
                </Campo>
              )}
              <Campo $error={!!errores.mensaje}>
                <span>Mensaje</span>
                <textarea id="contacto-mensaje" value={f.mensaje} maxLength={5000} onChange={e => set('mensaje', e.target.value)} placeholder="Cuéntanos en qué te podemos ayudar"
                  aria-invalid={!!errores.mensaje} aria-describedby={errores.mensaje ? 'err-mensaje' : undefined} />
                <div className="pie">
                  {errores.mensaje && <span className="error" id="err-mensaje">{errores.mensaje}</span>}
                  {!errores.mensaje && guardado && <Borrador>Borrador guardado en este navegador</Borrador>}
                  <span className="cuenta">{f.mensaje.length} / 5000</span>
                </div>
              </Campo>
              <Trampa tabIndex={-1} autoComplete="off" aria-hidden="true" name="sitio_web" value={f.sitio_web} onChange={e => set('sitio_web', e.target.value)} />
              <Enviar type="submit" disabled={estado === 'enviando'}>
                {estado === 'enviando' ? 'Enviando…' : c.texto_boton}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 3 10 14" /><path d="m21 3-7 18-4-7-7-4Z" /></svg>
              </Enviar>
            </form>
          )}
        </Tarjeta>
      </Cuerpo>
    </Raiz>
  );
};

export default ContactoFormSection;
